/**
 * PR13-C (Visual Feedback V2): the `?qa=feedback` QA gallery's trigger
 * list - a plain data table (id, label, category) so the "every listed
 * trigger actually exists, and only once" rule from the PR spec is a
 * directly-testable fact rather than something only a manual browser
 * pass could ever catch. `VisualFeedbackController.triggerQa(id)` is the
 * only thing that turns an id into an actual visual - this file never
 * touches Phaser, gameplay state, persistence, or the network: every
 * trigger here is visual-only by construction (the id is the entire
 * payload).
 */

export type QaGalleryCategory = "bite" | "chaos" | "wormhole" | "speed-trial" | "starfruit" | "gate" | "run-end" | "stress";

export interface QaGalleryTrigger {
  id: string;
  label: string;
  category: QaGalleryCategory;
}

export const QA_GALLERY_TRIGGERS: readonly QaGalleryTrigger[] = [
  { id: "safe", label: "SAFE", category: "bite" },
  { id: "rush-1", label: "RUSH ×1", category: "bite" },
  { id: "rush-2", label: "RUSH ×2", category: "bite" },
  { id: "rush-3", label: "RUSH ×3", category: "bite" },
  { id: "rush-4", label: "RUSH ×4", category: "bite" },
  { id: "rush-5", label: "RUSH ×5", category: "bite" },
  { id: "chaos-anticipation", label: "CHAOS anticip.", category: "chaos" },
  { id: "chaos-jackpot", label: "CHAOS>JACKPOT", category: "chaos" },
  { id: "chaos-wormhole", label: "CHAOS>WORMHOLE", category: "chaos" },
  { id: "chaos-shrink", label: "CHAOS>SHRINK", category: "chaos" },
  { id: "chaos-speed-trial", label: "CHAOS>SPD TRIAL", category: "chaos" },
  { id: "chaos-score-hit", label: "CHAOS>SCORE HIT", category: "chaos" },
  { id: "jackpot", label: "JACKPOT burst", category: "chaos" },
  { id: "shrink", label: "SHRINK contract", category: "chaos" },
  { id: "shrink-min", label: "SHRINK @ min len", category: "chaos" },
  { id: "speed-trial-start", label: "SPD TRIAL start", category: "speed-trial" },
  { id: "speed-trial-active", label: "SPD TRIAL badge", category: "speed-trial" },
  { id: "speed-trial-warning", label: "SPD TRIAL warn", category: "speed-trial" },
  { id: "speed-trial-success", label: "SPD TRIAL win", category: "speed-trial" },
  { id: "speed-trial-exit", label: "SPD TRIAL exit", category: "speed-trial" },
  { id: "wormhole-activate", label: "WORMHOLE start", category: "wormhole" },
  { id: "wormhole-active", label: "WORMHOLE badge", category: "wormhole" },
  { id: "wormhole-warning", label: "WORMHOLE warn", category: "wormhole" },
  { id: "wormhole-refresh", label: "WORMHOLE refresh", category: "wormhole" },
  { id: "wormhole-wrap-up", label: "Wrap: top", category: "wormhole" },
  { id: "wormhole-wrap-down", label: "Wrap: bottom", category: "wormhole" },
  { id: "wormhole-wrap-left", label: "Wrap: left", category: "wormhole" },
  { id: "wormhole-wrap-right", label: "Wrap: right", category: "wormhole" },
  { id: "wormhole-bite", label: "WORMHOLE BITE", category: "wormhole" },
  { id: "wormhole-clutch", label: "WORMHOLE CLUTCH", category: "wormhole" },
  { id: "starfruit-appear", label: "STARFRUIT shimmer", category: "starfruit" },
  { id: "starfruit-eat", label: "STARFRUIT eaten", category: "starfruit" },
  { id: "gate-temp-open", label: "Temp gate: 12s", category: "gate" },
  { id: "gate-temp-warning", label: "Temp gate: warn", category: "gate" },
  { id: "gate-temp-close", label: "Temp gate: closed", category: "gate" },
  { id: "gate-permanent-open", label: "Permanent gate", category: "gate" },
  { id: "gate-temp-to-permanent", label: "Temp>permanent", category: "gate" },
  { id: "gate-entry", label: "Gate entry", category: "gate" },
  { id: "death", label: "Death", category: "run-end" },
  { id: "level-complete", label: "Level complete", category: "run-end" },
  { id: "new-star-record", label: "NEW STAR RECORD!", category: "run-end" },
  { id: "new-high-score", label: "NEW HIGH SCORE!", category: "run-end" },
  { id: "both-records", label: "Both records", category: "run-end" },
  { id: "priority-stress", label: "Priority stress", category: "stress" },
  { id: "cleanup-reset", label: "Cleanup/reset", category: "stress" },
] as const;

export function qaGalleryTriggerIds(): readonly string[] {
  return QA_GALLERY_TRIGGERS.map((t) => t.id);
}

export function qaGalleryTriggersByCategory(category: QaGalleryCategory): readonly QaGalleryTrigger[] {
  return QA_GALLERY_TRIGGERS.filter((t) => t.category === category);
}
